/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * CampaignInfo sagas
 *
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.12.0
 */
import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { loadBanner } from '@/main/modules/banner/bannerActions';

import { loadTableMeta as loadTableMetaAction } from '../../components/Table/actions/tableActions';
import {
    API_CAMPAIGN_INFO_ACTIVATE,
    API_CAMPAIGN_INFO_ACTIVATE_TYPE,
    API_CAMPAIGN_INFO_GENERAL,
    API_CAMPAIGN_INFO_LINK_VISITED,
    API_CAMPAIGN_INFO_REMOVE,
} from '../../modules/app/Api';
import { makeSelectTranslationCommon } from '../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { hideLoader, showLoader } from '../../modules/loader/loaderActions';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalCloseAll, modalOpen, modalSetStatusAction } from '../../modules/modal/modalActions';
import { MODAL_CLOSE } from '../../modules/modal/modalConstants';
import { EModalStatus, IModalRenderProps } from '../../modules/modal/types/modalTypes';
import { AccessComponentKeyEnum } from '../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../modules/role/selectors/guardSelector';
import { ExternalRoutePath, openExternalRoute, openRoute } from '../../routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '../../routes/constants/routesConstants';
import { requestSaga } from '../../sagas/requestSaga';
import { stringFormatParams } from '../../utils/stringFormat';
import { ECampaignsType } from '../PageCampaign';
import { getCampaignsPageUrl } from '../PageCampaign/utils/getCampaignsPageUrl';
import {
    campaignInfoActivateSuccessAction,
    campaignInfoInitFailureAction,
    campaignInfoInitSuccessAction,
} from './campaignInfoActions';
import {
    CAMPAIGN_INFO_ACTIVATE_START,
    CAMPAIGN_INFO_GET_ACTIVATE_TYPE,
    CAMPAIGN_INFO_INIT_START,
    CAMPAIGN_INFO_LINK_VISITED,
    CAMPAIGN_INFO_REMOVE,
} from './campaignInfoConstant';
import { makeSelectCampaignInfoActions, makeSelectCampaignInfoGeneral } from './campaignInfoSelectors';
import CampaignInfoModalActivate from './components/CampaignInfoModalActivate';
import { mapCampaignInfoTypeToTableName } from './components/Table/CampaignInfoTableCells';
import {
    ECampaignInfoActivateType,
    ECampaignInfoRedirectType,
    ICampaignInfoActivateResponse,
    ICampaignInfoActivateTypeResponse,
    ICampaignInfoInitSuccessResponse,
    TCampaignInfoInitStartAction,
    TCampaignInfoLinkVisitedAction,
    TCampaignInfoRemoveResponse,
} from './types/campaignInfoActionTypes';
import { ECampaignInfoActions, ECampaignInfoType, ICampaignInfoGeneral } from './types/campaignInfoTypes';

/*
* -------------------------------------------
*         Init page
* -------------------------------------------
*/

export function* campaignInfoInitSagas({ payload }: TCampaignInfoInitStartAction): SagaIterator {
    try {
        const url = stringFormatParams(API_CAMPAIGN_INFO_GENERAL, { campaignId: payload.campaignId });
        const { buttonText, ...response }: ICampaignInfoInitSuccessResponse = yield call(requestSaga, url, 'GET');
        yield put(campaignInfoInitSuccessAction({
            ...response,
            text: { btnApply: buttonText },
            type: response.type === 'CLIENT' ? ECampaignInfoType.account : ECampaignInfoType.subscriber,
        }));
    } catch (e) {
        yield put(campaignInfoInitFailureAction());
    }
}

export function* campaignInfoLinkVisitedSagas({ payload }: TCampaignInfoLinkVisitedAction): SagaIterator {
    try {
        const url = stringFormatParams(API_CAMPAIGN_INFO_LINK_VISITED, { campaignId: payload.campaignId });
        yield call(requestSaga, url, 'GET');
    } catch (err) {
        console.error(err);
    }
}

/*
* -------------------------------------------
*         Activate \ delete campaign
* -------------------------------------------
*/

export function* campaignInfoRemoveSaga(): SagaIterator {
    try {
        // Set loading status for modal
        yield put(modalSetStatusAction({ status: EModalStatus.processing }));

        const generalInfo: ICampaignInfoGeneral = yield select(makeSelectCampaignInfoGeneral());
        const actions: ECampaignInfoActions[] = yield select(makeSelectCampaignInfoActions());
        const isInformCampaignType = !actions.includes(ECampaignInfoActions.participantList)
            && !actions.includes(ECampaignInfoActions.selfManage);
        const url = stringFormatParams(API_CAMPAIGN_INFO_REMOVE, {
            campaignId: generalInfo.id,
            fromBasket: !(actions.includes(ECampaignInfoActions.selfManage) || isInformCampaignType),
        });

        const { remain }: TCampaignInfoRemoveResponse = yield call(requestSaga, url, 'DELETE');

        yield put(modalCloseAll());

        remain
            ? yield put(loadTableMetaAction({ tableName: mapCampaignInfoTypeToTableName[generalInfo.type] }))
            : yield call(campaignInfoRedirectToCampaigns);

        yield put(loadBanner());
    } catch (e) {
        yield put(modalSetStatusAction({ status: EModalStatus.failure }));
    }
}

export function* campaignInfoActivateTypeSaga(): SagaIterator {
    const generalInfo: ICampaignInfoGeneral = yield select(makeSelectCampaignInfoGeneral());

    try {
        yield put(showLoader());

        const actions: ECampaignInfoActions[] = yield select(makeSelectCampaignInfoActions());
        const url = stringFormatParams(API_CAMPAIGN_INFO_ACTIVATE_TYPE, {
            campaignId: generalInfo.id,
            fromBasket: !actions.includes(ECampaignInfoActions.selfManage),
        });
        const { campaignActivationType }: ICampaignInfoActivateTypeResponse = yield call(requestSaga, url, 'GET');

        if (!campaignActivationType) {
            yield call(campaignInfoActivateSaga, false);
            return;
        }

        switch (campaignActivationType) {
            case ECampaignInfoActivateType.inquiry:
                yield put(hideLoader());
                yield put(modalOpen({
                    render: (props: IModalRenderProps) => createElement(CampaignInfoModalActivate, {
                        onCloseModal: props.onCloseModal,
                        type: generalInfo.type,
                    }),
                }));
                break;
            default:
                yield call(campaignInfoActivateSaga, false);
                break;
        }
    } catch (e) {
        yield put(hideLoader());
        const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
        yield put(modalOpen({
            render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
                onCloseModal: props.onCloseModal,
                translation: translation.modal,
            }),
        }));
    }
}

export function* campaignInfoActivateSaga(isInquiryType = true): SagaIterator {
    try {
        if (isInquiryType) {
            yield put(modalSetStatusAction({ status: EModalStatus.processing }));
        }

        const generalInfo: ICampaignInfoGeneral = yield select(makeSelectCampaignInfoGeneral());
        const actions: ECampaignInfoActions[] = yield select(makeSelectCampaignInfoActions());
        const url = stringFormatParams(API_CAMPAIGN_INFO_ACTIVATE, {
            campaignId: generalInfo.id,
            fromBasket: !actions.includes(ECampaignInfoActions.selfManage),
        });

        const { redirect, remain, campaignInquiryId }: ICampaignInfoActivateResponse =
            yield call(requestSaga, url, 'POST');

        yield put(hideLoader());
        if (redirect) {
            yield put(modalCloseAll());
            yield call(campaignInfoRedirectToProducts, redirect);
            return;
        }

        // Открываем модально окно с успешно сформированным обращением
        if (campaignInquiryId && isInquiryType) {
            yield put(campaignInfoActivateSuccessAction({ campaignInquiryId }));
            yield put(modalSetStatusAction({ status: EModalStatus.success }));

            yield take(MODAL_CLOSE);
        } else {
            yield put(modalCloseAll());
        }

        remain
            ? yield put(loadTableMetaAction({ tableName: mapCampaignInfoTypeToTableName[generalInfo.type] }))
            : yield call(campaignInfoRedirectToCampaigns);

        yield put(loadBanner());

    } catch (e) {
        if (isInquiryType) {
            yield put(modalSetStatusAction({ status: EModalStatus.failure }));
        } else {
            yield put(hideLoader());
            const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
            yield put(modalOpen({
                render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    translation: translation.modal,
                }),
            }));
        }
    } 
}

export function* campaignInfoRedirectToProducts(
    redirect: Required<ICampaignInfoActivateResponse>['redirect'],
): SagaIterator {
    const isProductEnabled = yield select(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageProducts));

    isProductEnabled
        // open product master on react
        ? openRoute(RoutePath.wizardProductOfferingsFromCampaigns.replace(CLIENT_ID, redirect.id))
        : redirect.type === ECampaignInfoRedirectType.service
        ? openExternalRoute(ExternalRoutePath.mobileServiceFromCampaign)
        : openExternalRoute(ExternalRoutePath.mobilePlanFromCampaign);
}

export function* campaignInfoRedirectToCampaigns(): SagaIterator {
    const generalInfo: ICampaignInfoGeneral = yield select(makeSelectCampaignInfoGeneral());
    const url = getCampaignsPageUrl(
        generalInfo.type === ECampaignInfoType.account ? ECampaignsType.account : ECampaignsType.subscriber,
    );
    openRoute(url);
}

/*
* -------------------------------------------
*         Campaign Info Sagas
* -------------------------------------------
*/

export function* campaignInfoSagas(): SagaIterator {
    yield all([
        takeEvery(CAMPAIGN_INFO_ACTIVATE_START, campaignInfoActivateSaga),
        takeEvery(CAMPAIGN_INFO_GET_ACTIVATE_TYPE, campaignInfoActivateTypeSaga),
        takeEvery(CAMPAIGN_INFO_INIT_START, campaignInfoInitSagas),
        takeEvery(CAMPAIGN_INFO_LINK_VISITED, campaignInfoLinkVisitedSagas),
        takeEvery(CAMPAIGN_INFO_REMOVE, campaignInfoRemoveSaga),
    ]);
}
